import {
	CampaignEntitlement,
	FunnelCampaign,
	FunnelType,
} from '@app/components/modules/funnel/types';

import { ENTITLEMENT_REQUIREMENT_LABELS } from '@app/constants/modules/funnel';
import { WorkspacePlansEnum } from '@app/constants/modules/workspace';

const ADVANCE_FUNNEL_PLANS: string[] = [
	WorkspacePlansEnum.ADVANCE_1_5,
	WorkspacePlansEnum.ADVANCE_2_0,
	WorkspacePlansEnum.ADVANCE,
	WorkspacePlansEnum.DIRECT_ADMISSIONS,
];

// Structural type so both FunnelCampaign and CampaignType-shaped campaigns fit
type EntitlementHolder =
	| { entitlement?: CampaignEntitlement | null }
	| null
	| undefined;

function getFunnelIdByCampaignId(campaignId: string, funnels: FunnelType[]) {
	return funnels.find((funnel) =>
		funnel.campaigns?.some(
			(campaign: FunnelCampaign) => campaign._id === campaignId,
		),
	)?._id;
}

// Campaigns without an entitlement field behave as allowed (fail-open)
function checkIsCampaignEntitlementAllowed(campaign: EntitlementHolder) {
	const entitlement = campaign?.entitlement;

	if (!entitlement) return true;

	return entitlement.allowed !== false;
}

// `allowed` is authoritative, groups only explain why it is false.
// Groups are alternative paths (OR), requirements within a group are AND.
function getUnsatisfiedEntitlementRequirementIds(
	campaign: EntitlementHolder,
): string[] {
	const entitlement = campaign?.entitlement;

	if (!entitlement || entitlement.allowed) return [];

	const ids = new Set<string>();

	(entitlement.requirementGroups ?? [])
		.filter((group) => !group.satisfied)
		.forEach((group) => {
			(group.requirements ?? [])
				.filter((requirement) => !requirement.satisfied)
				.forEach((requirement) => ids.add(requirement.id));
		});

	return Array.from(ids);
}

function getEntitlementDisabledReasons(campaign: EntitlementHolder): string[] {
	return getUnsatisfiedEntitlementRequirementIds(campaign).map(
		(id) => ENTITLEMENT_REQUIREMENT_LABELS[id] ?? id.replace(/-/g, ' '),
	);
}

function getNotEntitledFunnelCampaignIds(funnels: FunnelType[]): string[] {
	return funnels.flatMap((funnel) =>
		(funnel.campaigns ?? [])
			.filter((campaign) => !checkIsCampaignEntitlementAllowed(campaign))
			.map((campaign) => campaign._id),
	);
}

function getNotEntitledFunnelCampaignAudienceIds(
	funnels: FunnelType[],
): string[] {
	const audienceIds = funnels.flatMap((funnel) =>
		(funnel.campaigns ?? [])
			.filter((campaign) => !checkIsCampaignEntitlementAllowed(campaign))
			.map((campaign) => campaign.audience)
			.filter((audienceId): audienceId is string => Boolean(audienceId)),
	);

	return Array.from(new Set(audienceIds));
}

function checkIsAdvanceFunnel(
	funnel: { nicheCorePlan?: string } | null | undefined,
): boolean {
	return ADVANCE_FUNNEL_PLANS.includes(funnel?.nicheCorePlan ?? '');
}

function checkIsDAFunnel(
	funnel: { nicheCorePlan?: string } | null | undefined,
): boolean {
	return funnel?.nicheCorePlan === WorkspacePlansEnum.DIRECT_ADMISSIONS;
}

function getAdvanceFunnels<T extends { nicheCorePlan?: string }>(
	funnels: T[],
): T[] {
	return funnels.filter(checkIsAdvanceFunnel);
}

function getEntitledFunnels<T extends { campaigns?: FunnelCampaign[] | null }>(
	funnels: T[],
): T[] {
	return funnels.map((funnel) => ({
		...funnel,
		campaigns: funnel.campaigns
			? funnel.campaigns.filter((campaign) =>
					checkIsCampaignEntitlementAllowed(campaign),
				)
			: funnel.campaigns,
	}));
}

export {
	getFunnelIdByCampaignId,
	checkIsCampaignEntitlementAllowed,
	getUnsatisfiedEntitlementRequirementIds,
	getEntitlementDisabledReasons,
	getNotEntitledFunnelCampaignIds,
	getNotEntitledFunnelCampaignAudienceIds,
	getEntitledFunnels,
	checkIsAdvanceFunnel,
	checkIsDAFunnel,
	getAdvanceFunnels,
};
